import type createClient from 'openapi-fetch';
import type { paths } from '../../types/yakubato-lp-api';

export type OpenApiFetchClient = ReturnType<typeof createClient<paths>>;

export type RequestBody<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = paths[Path][Method] extends {
  requestBody: { content: { 'application/json': infer B } };
}
  ? B
  : never;

export type PathParams<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = paths[Path][Method] extends {
  parameters: { path: infer P };
}
  ? P
  : never;

export type QueryParams<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = paths[Path][Method] extends {
  parameters: { query?: infer Q };
}
  ? NonNullable<Q>
  : never;

export type ApiResponse<T = unknown> = {
  status: number;
  data: T;
  headers: Headers;
};

export type ApiResponseNoContent = {
  status: number;
  data: undefined;
  headers: Headers;
};

export class ApiError extends Error {
  response: { status: number; data: unknown; headers: Headers };

  constructor(status: number, data: unknown, headers: Headers) {
    super(`Request failed with status code ${status}`);
    this.name = 'ApiError';
    this.response = { status, data, headers };
  }
}

export async function request<T>(
  result: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<ApiResponse<T>> {
  const { data, error, response } = await result;
  if (response.ok) {
    return {
      status: response.status,
      data: data as T,
      headers: response.headers,
    };
  }
  throw new ApiError(response.status, error, response.headers);
}

export async function requestNoContent(
  result: Promise<{ data?: unknown; error?: unknown; response: Response }>,
): Promise<ApiResponseNoContent> {
  const { error, response } = await result;
  if (response.ok) {
    return {
      status: response.status,
      data: undefined,
      headers: response.headers,
    };
  }
  throw new ApiError(response.status, error, response.headers);
}
